import { X } from 'lucide-react';
import React from 'react';
import { STATUS_CONFIG } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import { useDataStore } from '../stores/useDataStore';
import { Category, RecommendationStatus } from '../types';
import { StatusStamp } from './StatusStamp';

interface FilterState {
  categories: Category[];
  statuses: RecommendationStatus[];
  userIds: string[];
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterState;
  setFilters: (f: FilterState) => void;
}

export const FilterModal: React.FC<FilterModalProps> = ({ 
  isOpen, onClose, filters, setFilters 
}) => {
  const { currentUser } = useAuth();
  const { users } = useDataStore();
  const currentUserId = currentUser?.uid || '';
  
  const relevantUsers = users.filter(u => u.uid === currentUserId || (currentUser?.friends || []).includes(u.uid));
  if (!isOpen) return null;

  // Toggle Helpers
  const toggleCategory = (c: Category) => {
    setFilters({
      ...filters,
      categories: filters.categories.includes(c) 
        ? filters.categories.filter(x => x !== c) 
        : [...filters.categories, c]
    });
  };

  const toggleStatus = (s: RecommendationStatus) => {
    setFilters({
      ...filters,
      statuses: filters.statuses.includes(s)
        ? filters.statuses.filter(x => x !== s)
        : [...filters.statuses, s]
    });
  };

  const toggleUser = (uid: string) => {
    setFilters({
      ...filters,
      userIds: filters.userIds.includes(uid)
        ? filters.userIds.filter(x => x !== uid)
        : [...filters.userIds, uid]
    });
  };

  const clearAll = () => {
    setFilters({ categories: [], statuses: [], userIds: [] });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-brand-dark/60 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-md rounded-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
        onClick={e => e.stopPropagation()}
      >
        
        {/* Header */}
        <div className="p-4 border-b border-brand-cream-dark flex justify-between items-center bg-white">
           <h3 className="font-display font-extrabold text-xl text-brand-dark">Filter Map</h3>
           <button onClick={onClose} className="p-2 hover:bg-brand-cream-dark rounded-full"><X size={20}/></button>
        </div>

        <div className="p-5 space-y-6 overflow-y-auto bg-brand-cream">
            
            {/* Categories */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Category</h4>
                <div className="flex gap-3">
                    {(Object.values(Category) as Category[]).map(cat => {
                        const isActive = filters.categories.includes(cat);
                        return (
                            <button 
                                key={cat}
                                onClick={() => toggleCategory(cat)}
                                className={`flex-1 py-3 rounded-xl border-2 font-bold text-sm transition-all ${isActive ? 'border-brand-dark bg-brand-dark text-white' : 'border-brand-cream-dark bg-white text-brand-dark-light hover:border-brand-dark'}`}
                            >
                                {cat === Category.FOOD ? 'Food' : 'Drinks'}
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Status */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Verdict</h4>
                <div className="grid grid-cols-2 gap-2">
                    {(Object.values(RecommendationStatus) as RecommendationStatus[]).map((status, i, arr) => {
                        const config = STATUS_CONFIG[status];
                        const isActive = filters.statuses.includes(status);
                        // Odd count: let the last tile span the full row so the grid stays even.
                        const isLastOdd = i === arr.length - 1 && arr.length % 2 === 1;
                        return (
                            <button
                                key={status}
                                onClick={() => toggleStatus(status)}
                                className={`rounded-lg transition-transform active:scale-95 ${isLastOdd ? 'col-span-2' : ''}`}
                            >
                                <StatusStamp
                                    status={status}
                                    size="md"
                                    rotate={false}
                                    filled={isActive}
                                    animate={isActive}
                                    label={config.label}
                                    labelMaxWidth="160px"
                                    className="w-full justify-center min-h-[46px]"
                                />
                            </button>
                        )
                    })}
                </div>
            </div>

            {/* Friends */}
            <div className="space-y-3">
                <h4 className="text-xs font-bold text-brand-dark-light uppercase tracking-wider">Recommended By</h4>
                <div className="flex flex-wrap gap-2">
                    {relevantUsers.map(user => {
                         const isActive = filters.userIds.includes(user.uid);
                         const isMe = user.uid === currentUserId;
                         return (
                            <button
                                key={user.uid}
                                onClick={() => toggleUser(user.uid)}
                                className={`pl-1 pr-3 py-1 rounded-full border flex items-center gap-2 transition-all ${isActive ? 'bg-brand-red border-brand-red text-white shadow-md' : 'border-brand-cream-dark bg-white text-brand-dark hover:border-brand-dark'}`}
                            >
                                <img src={user.photoURL} className="w-6 h-6 rounded-full bg-brand-cream-dark" alt={user.displayName} />
                                <span className="text-xs font-bold">{isMe ? 'Me' : user.displayName}</span>
                            </button>
                         )
                    })}
                </div>
            </div>

        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-cream-dark bg-white flex gap-3">
            <button 
                onClick={clearAll}
                className="flex-1 py-3 text-brand-dark-light font-bold hover:bg-brand-cream-dark rounded-xl transition"
            >
                Clear All
            </button>
            <button 
                onClick={onClose}
                className="flex-[2] py-3 bg-brand-red text-white rounded-xl font-bold shadow-lg hover:bg-brand-red-hover transition active:scale-95"
            >
                Show Results
            </button>
        </div>
      </div>
    </div>
  );
};